import * as React from 'react';
import styled, { css, DefaultTheme } from 'styled-components';
import { memoizeFunction } from '@agilepoint/utils-common';
import { IBaseComponentProps } from '../../types';
import { ITagProps } from './tag';
import { stopEventPropagation } from './utils';
import { StackPanel } from '../../stack-panel';
import { Icon } from '../../icon';
import { selectThemeSemantics } from '../../../styling';
import { Typography } from '../../typography';
import { TagItemState } from '../theme';

interface ITagContentProps extends Pick<ITagProps, 'disabled' | 'isStarredItem' | 'item'>, IBaseComponentProps<HTMLDivElement> {
    onEditItem(): void;
    onRemoveItem(): void;
}

const Label = styled(Typography.Small).attrs(() => ({
    className: String('ap-tagInput-tagLabel')
}))`
    max-width: 100px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
`;

const commonIconStyle = css`
    padding: ${selectThemeSemantics(({ gap }) => gap.xs)};
    fill: ${({ theme: { tagInput: { colors: { tagItem } } } }) => tagItem.default.iconColor};
`;

const RemoveButton = styled(Icon).attrs({
    className: String('ap-tagInput-removeTagButton'),
    iconName: 'ap-close.outline',
    iconSize: '12'
}) <{ disabled?: boolean }>`
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    ${commonIconStyle}
`;

const StarIcon = styled(Icon).attrs({
    className: String('ap-tagInput-starIcon'),
    iconName: 'ap-star.solid',
    iconSize: '16'
})`
    ${commonIconStyle}
`;

const TagContentBase = React.memo((props: ITagContentProps) => {
    const { item, className, elementRef, disabled, isStarredItem, onEditItem, onRemoveItem } = props;

    const _onDoubleClick = React.useCallback((ev: React.MouseEvent) => {
        stopEventPropagation(ev);
        if (disabled) {
            return;
        }
        onEditItem();
    }, [disabled, onEditItem]);

    const _onRemoveClick = React.useCallback((ev: React.MouseEvent) => {
        stopEventPropagation(ev);
        if (disabled) {
            return;
        }
        onRemoveItem();
    }, [disabled, onRemoveItem]);

    return (
        <StackPanel
            ref={elementRef}
            className={className}
            title={item.text}
            alignment="center"
            gap="8"
            onClick={stopEventPropagation}
            onDoubleClick={_onDoubleClick}>
            <StarIcon hidden={!isStarredItem} />
            <Label>{item.text}</Label>
            <RemoveButton disabled={disabled} onClick={_onRemoveClick} />
        </StackPanel>
    );
});

TagContentBase.displayName = 'TagContentBase';

const generateTagItemStyleByState = memoizeFunction((state: TagItemState) => {
    const getStyles = ({ tagInput: { colors: { tagItem } } }: DefaultTheme) => tagItem[state];
    return css`
        background-color: ${({ theme }) => getStyles(theme).backgroundColor};
        border-color: ${({ theme }) => getStyles(theme).borderColor};
        color: ${({ theme }) => getStyles(theme).foregroundColor};
        ${RemoveButton} {
            fill: ${({ theme }) => getStyles(theme).iconColor};
        }
        ${StarIcon} {
            fill: ${({ theme }) => getStyles(theme).iconColor};
        }
`;
});

const getTagItemStyle = memoizeFunction((state: TagItemState) => ({ disabled }: ITagContentProps) => generateTagItemStyleByState(disabled ? 'disabled' : state));


export const TagContent = styled(TagContentBase).attrs(() => ({
    className: String('ap-tagInput-tag')
}))`
    user-select: none;
    animation: appear .1s ease;
    border-style: solid;
    box-sizing: border-box;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'default')};
    box-shadow: ${({ theme: { tagInput: { composites: { tagItem } } } }) => tagItem.boxShadow};
    border-radius: ${({ theme: { tagInput: { composites: { tagItem } } } }) => tagItem.borderRadius};
    margin: ${selectThemeSemantics(({ gap }) => `${gap.s} ${gap.l} ${gap.s} ${gap.none}`)};
    padding: ${selectThemeSemantics(({ gap }) => `${gap.m} ${gap.l}`)};
    border-width: ${({ theme: { tagInput: { composites: { tagItem } } } }) => tagItem.borderWidth};
    ${getTagItemStyle('default')}
    
    :hover {
        ${getTagItemStyle('hovered')}
    }

    @keyframes appear {
        from { transform: scale(0); }
        to { transform: scale(1); }
    }
`;

TagContent.displayName = 'TagContent';
